import * as React from "react";
import { concat, size, some } from "lodash";
import { useThrottle } from "@lkf/hooks";
import BlockedProjectsModal from "@radarly/app/containers/modals/blocked-project";
import ContactProjectManagerModal from "@radarly/app/containers/modals/contact-manager";
import Switcher from "@radarly/components/navigation/switcher";
import Layout from "@radarly/components/navigation/switcher/layout";
import Projects from "@radarly/components/navigation/switcher/projects";
import { useClosePanel } from "@radarly/core/panel/use-cases";
import {
  useCurrentProject,
  useProjectWorkspaces,
} from "@radarly/core/project/use-cases";
import {
  useCurrentUser,
  useCurrentUserProjects,
} from "@radarly/core/user/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { useCurrentWorkspace } from "@radarly/core/workspace/use-cases";
import { IRouterService } from "@radarly/shared/router";
import useProject from "./project";
import { NavigationSwitcherSearch } from "./search";

interface IProps {
  router: IRouterService;
}

function useCurrentProjects() {
  const project = useCurrentProject();
  const projects = useCurrentUserProjects();
  if (!some(projects, { id: project.id })) {
    // @ts-ignore
    return concat(projects, [project]);
  } else {
    return projects;
  }
}

const RadarlySharedNavigationSwitcher = ({ router }: IProps) => {
  const [search, setSearch] = React.useState("");
  const throttledSearch = useThrottle(search, 500);
  const projects = useCurrentProjects();
  const currentProject = useCurrentProject();
  const workspace = useCurrentWorkspace();
  const locale = useCurrentUserLocale();
  const user = useCurrentUser();
  const { project, setProject } = useProject(currentProject.id, projects);
  const { workspaces, loading } = useProjectWorkspaces(project.id);
  const onClosePanel = useClosePanel();
  return (
    <ContactProjectManagerModal project={project}>
      {(openContactProjectManagerModal) => (
        <BlockedProjectsModal
          project={project}
          onValidate={openContactProjectManagerModal}
        >
          {(openBlockedProjectModal) => (
            <Layout>
              {(height) => (
                <Switcher
                  search={search}
                  onClose={() => onClosePanel("switcher")}
                  onChangeSearch={setSearch}
                  locale={locale}
                >
                  {size(throttledSearch) > 1 ? (
                    <NavigationSwitcherSearch
                      height={height}
                      router={router}
                      search={throttledSearch}
                      onClickItem={() => onClosePanel("switcher")}
                    />
                  ) : (
                    <Projects
                      projects={projects}
                      project={project ? project.id : 0}
                      workspaces={
                        currentProject.id === project.id
                          ? currentProject.dashboards
                          : workspaces
                      }
                      workspaceId={workspace ? workspace.id : 0}
                      onChange={setProject}
                      locale={locale}
                      height={height}
                      loading={loading}
                      user={user}
                      clickBlockedProjectHelp={openBlockedProjectModal}
                      onClickWorkspace={() => onClosePanel("switcher")}
                      router={router}
                    />
                  )}
                </Switcher>
              )}
            </Layout>
          )}
        </BlockedProjectsModal>
      )}
    </ContactProjectManagerModal>
  );
};

export default RadarlySharedNavigationSwitcher;
